import { useRef, useEffect, useState, lazy, Suspense, useCallback } from "react";
import { PLANS } from "@/lib/constants";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/settings/LanguageSelector";
import { getPlanOption, formatPlanPriceShort } from "@/lib/pricing";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Seo } from "@/components/seo/Seo";
import { RoadmapPreview } from "@/components/landing/RoadmapPreview";
import { getFennieArtwork } from "@/lib/fennieStageAssets";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  Shield,
  Lock,
  Users,
  ChevronDown,
  Sparkles,
  Check,
  Eye,
  TrendingDown,
  Brain,
  Target,
  Mic,
  Camera,
  BarChart3,
  CreditCard,
  Wallet,
  CircleDot,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// Lazy load AuthForm
const AuthForm = lazy(() =>
  import("@/components/auth/AuthForm").then((m) => ({ default: m.AuthForm }))
);

/* ─── Navbar ─── */
function LandingNav({ onCTA }: { onCTA: () => void }) {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <nav
      ref={navRef}
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ease-out
        ${scrolled
          ? "bg-[#0D0D12]/80 backdrop-blur-xl border border-[#2A2A35]/60 shadow-2xl shadow-black/40"
          : "bg-transparent border border-transparent"
        }
        rounded-[2rem] px-3 py-2 sm:px-6 sm:py-3 flex items-center gap-2 sm:gap-6 max-w-3xl w-[95%] sm:w-auto`}
    >
      <span className="font-semibold text-[#FAF8F5] text-lg tracking-tight">{t("nav.brand")}</span>
      <div className="hidden sm:flex items-center gap-5 text-sm text-[#FAF8F5]/60">
        <a href="#features" className="hover:text-[#C9A84C] transition-colors hover:-translate-y-px">{t("nav.features")}</a>
        <a href="#companion" className="hover:text-[#C9A84C] transition-colors hover:-translate-y-px">{t("nav.companion")}</a>
        <a href="#pricing" className="hover:text-[#C9A84C] transition-colors hover:-translate-y-px">{t("nav.pricing")}</a>
        <a href="#faq" className="hover:text-[#C9A84C] transition-colors hover:-translate-y-px">{t("nav.faq")}</a>
      </div>
      <LanguageSelector className="hidden sm:flex !px-0 !py-0" />
      <button
        onClick={onCTA}
        className="ml-auto bg-[#C9A84C] text-[#0D0D12] text-sm font-semibold px-5 py-2 rounded-[1.5rem]
          hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200 whitespace-nowrap"
      >
        {t("nav.startTrial")}
      </button>
    </nav>
  );
}

/* ─── Noise Overlay ─── */
function NoiseOverlay() {
  return (
    <div className="pointer-events-none fixed inset-0 z-[100] opacity-[0.035]" aria-hidden>
      <svg width="100%" height="100%">
        <filter id="noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noise)" />
      </svg>
    </div>
  );
}

/* ─── Hero ─── */
function HeroSection({ onCTA }: { onCTA: () => void }) {
  const { t } = useTranslation("landing");
  const sectionRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const trustRef = useRef<HTMLDivElement>(null);
  const panelsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      // Split headline words
      if (headlineRef.current) {
        const words = headlineRef.current.querySelectorAll(".hero-word");
        tl.from(words, { y: 80, opacity: 0, stagger: 0.08, duration: 1 }, 0.3);
      }
      tl.from(subRef.current, { y: 40, opacity: 0, duration: 0.8 }, 0.8);
      tl.from(ctaRef.current, { y: 30, opacity: 0, duration: 0.6 }, 1.0);
      tl.from(trustRef.current, { y: 20, opacity: 0, duration: 0.5 }, 1.2);

      // Floating panels
      if (panelsRef.current) {
        const panels = panelsRef.current.querySelectorAll(".hero-panel");
        tl.from(panels, { y: 60, opacity: 0, stagger: 0.15, duration: 0.8 }, 1.0);

        // Continuous float
        panels.forEach((panel, i) => {
          gsap.to(panel, {
            y: `${(i % 2 === 0 ? -12 : 12)}`,
            duration: 3 + i * 0.5,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100dvh] flex items-end pb-16 sm:pb-24 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D12] via-[#0D0D12] to-[#1a1a24]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#C9A84C]/5 via-transparent to-transparent" />

      {/* Floating product UI panels */}
      <div ref={panelsRef} className="absolute inset-0 overflow-hidden hidden lg:block">
        {/* Spending Dashboard panel */}
        <div className="hero-panel absolute top-[15%] end-[8%] w-72 bg-[#1a1a24]/80 backdrop-blur-md border border-[#2A2A35]/40 rounded-[1.5rem] p-5 shadow-2xl shadow-black/30">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-[#C9A84C]" />
            <span className="text-xs font-mono text-[#FAF8F5]/50">{t("hero.panel.spendingOverview")}</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#FAF8F5]/70">{t("hero.panel.thisMonth")}</span>
              <span className="font-mono text-[#FAF8F5] text-sm">$2,847</span>
            </div>
            <div className="h-2 bg-[#2A2A35] rounded-full overflow-hidden">
              <div className="h-full w-[62%] bg-gradient-to-r from-[#C9A84C] to-[#C9A84C]/60 rounded-full" />
            </div>
            <div className="flex justify-between text-xs text-[#FAF8F5]/40">
              <span>{t("hero.panel.budgetPct")}</span>
              <span>{t("hero.panel.limit")}</span>
            </div>
          </div>
        </div>

        {/* AI Insights panel */}
        <div className="hero-panel absolute top-[40%] end-[3%] w-64 bg-[#1a1a24]/80 backdrop-blur-md border border-[#2A2A35]/40 rounded-[1.5rem] p-4 shadow-2xl shadow-black/30">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-[#C9A84C]" />
            <span className="text-xs font-mono text-[#FAF8F5]/50">{t("hero.panel.aiInsight")}</span>
          </div>
          <p className="text-sm text-[#FAF8F5]/80 leading-relaxed">
            {t("hero.panel.aiInsightText")}
          </p>
        </div>

        {/* Savings Goal panel */}
        <div className="hero-panel absolute bottom-[25%] end-[12%] w-60 bg-[#1a1a24]/80 backdrop-blur-md border border-[#2A2A35]/40 rounded-[1.5rem] p-4 shadow-2xl shadow-black/30">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[#C9A84C]" />
            <span className="text-xs font-mono text-[#FAF8F5]/50">{t("hero.panel.savingsGoal")}</span>
          </div>
          <p className="text-sm text-[#FAF8F5]/70 mb-2">{t("hero.panel.emergencyFund")}</p>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="font-mono text-lg text-[#FAF8F5]">$8,420</span>
            <span className="text-xs text-[#FAF8F5]/40">/ $10,000</span>
          </div>
          <div className="h-1.5 bg-[#2A2A35] rounded-full overflow-hidden">
            <div className="h-full w-[84%] bg-[#C9A84C] rounded-full" />
          </div>
        </div>

        {/* Companion panel */}
        <div className="hero-panel absolute bottom-[15%] end-[28%] w-48 bg-[#1a1a24]/80 backdrop-blur-md border border-[#C9A84C]/20 rounded-[1.5rem] p-4 shadow-2xl shadow-[#C9A84C]/10">
          <div className="text-center">
            <img
              src={getFennieArtwork("common", "healthy")}
              alt="Fennie companion"
              className="w-16 h-16 mx-auto object-contain"
              draggable={false}
            />
            <p className="text-xs text-[#C9A84C] font-medium mt-2">{t("hero.panel.fennieProud")}</p>
            <p className="text-[10px] text-[#FAF8F5]/40 mt-1">{t("hero.panel.streak")}</p>
          </div>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 w-full">
        <h1 ref={headlineRef} className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tight mb-6">
          <span className="hero-word inline-block text-[#FAF8F5]">{t("hero.titleWord1")}</span>{" "}
          <span className="hero-word inline-block text-[#FAF8F5]">{t("hero.titleWord2")}</span>
          <br />
          <span className="hero-word inline-block text-[#FAF8F5]">{t("hero.titleWord3")}</span>{" "}
          <span className="hero-word inline-block font-['Playfair_Display'] italic text-[#C9A84C]">{t("hero.titleWord4")}</span>
        </h1>

        <p ref={subRef} className="text-lg sm:text-xl text-[#FAF8F5]/75 max-w-xl leading-relaxed mb-8">
          {t("hero.subtitle")}
        </p>

        <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 mb-10">
          <button
            onClick={onCTA}
            className="group bg-[#C9A84C] text-[#0D0D12] font-semibold text-base px-8 py-4 rounded-[2rem]
              hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 flex items-center gap-2
              shadow-lg shadow-[#C9A84C]/20"
          >
            {t("hero.startTrial")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
          </button>
          <a
            href="#features"
            className="text-[#FAF8F5]/60 font-medium text-base px-8 py-4 rounded-[2rem] border border-[#2A2A35]
              hover:border-[#C9A84C]/40 hover:text-[#FAF8F5] transition-all duration-200 text-center"
          >
            {t("hero.seeHowItWorks")}
          </a>
        </div>

        <div ref={trustRef} className="flex flex-wrap gap-6 text-sm text-[#FAF8F5]/35">
          <span className="flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> {t("hero.trustPrivacy")}</span>
          <span className="flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> {t("hero.trustNoBank")}</span>
          <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {t("hero.trustFamilies")}</span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <ChevronDown className="w-5 h-5 text-[#FAF8F5]/20" />
      </div>
    </section>
  );
}

/* ─── Feature Card: Diagnostic Shuffler ─── */
function DiagnosticShuffler() {
  const { t } = useTranslation("landing");
  const [activeIndex, setActiveIndex] = useState(0);
  const labels = [t("diagnostic.label1"), t("diagnostic.label2"), t("diagnostic.label3")];
  const amounts = ["$247/mo", "$890/mo", "$312/mo"];
  const colors = ["#C9A84C", "#FAF8F5", "#C9A84C"];

  useEffect(() => {
    const interval = setInterval(() => setActiveIndex((i) => (i + 1) % 3), 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-64 flex items-center justify-center">
      {labels.map((label, i) => {
        const offset = (i - activeIndex + 3) % 3;
        return (
          <div
            key={label}
            className="absolute w-56 bg-[#1a1a24] border border-[#2A2A35]/60 rounded-[1.5rem] p-5 transition-all duration-700 ease-out"
            style={{
              transform: `translateY(${offset * 24 - 24}px) scale(${1 - offset * 0.05})`,
              opacity: offset === 0 ? 1 : 0.5 - offset * 0.15,
              zIndex: 3 - offset,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4" style={{ color: colors[i] }} />
              <span className="text-xs font-mono text-[#FAF8F5]/40">{t("diagnostic.detected")}</span>
            </div>
            <p className="text-sm text-[#FAF8F5] font-medium">{label}</p>
            <p className="font-mono text-lg mt-1" style={{ color: colors[i] }}>{amounts[i]}</p>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Feature Card: Telemetry Typewriter ─── */
function TelemetryTypewriter() {
  const { t } = useTranslation("landing");
  const lines = [
    t("telemetry.line1"),
    t("telemetry.line2"),
    t("telemetry.line3"),
    t("telemetry.line4"),
  ];
  const [displayedLines, setDisplayedLines] = useState<string[]>([]);
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);

  useEffect(() => {
    if (currentLine >= lines.length) {
      const timeout = setTimeout(() => {
        setDisplayedLines([]);
        setCurrentLine(0);
        setCurrentChar(0);
      }, 3000);
      return () => clearTimeout(timeout);
    }

    const timeout = setTimeout(() => {
      if (currentChar < lines[currentLine].length) {
        setCurrentChar((c) => c + 1);
      } else {
        setDisplayedLines((prev) => [...prev, lines[currentLine]]);
        setCurrentLine((l) => l + 1);
        setCurrentChar(0);
      }
    }, currentChar < lines[currentLine]?.length ? 35 : 800);

    return () => clearTimeout(timeout);
  }, [currentLine, currentChar]);

  return (
    <div className="bg-[#0D0D12] border border-[#2A2A35]/40 rounded-[1.5rem] p-5 h-64 overflow-hidden font-mono text-sm">
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-4 h-4 text-[#C9A84C]" />
        <span className="text-xs text-[#FAF8F5]/40">{t("telemetry.aiAnalysis")}</span>
      </div>
      <div className="space-y-2">
        {displayedLines.map((line, i) => (
          <p key={i} className="text-[#FAF8F5]/60 text-xs leading-relaxed">
            <span className="text-[#C9A84C] me-2">›</span>{line}
          </p>
        ))}
        {currentLine < lines.length && (
          <p className="text-[#FAF8F5]/80 text-xs leading-relaxed">
            <span className="text-[#C9A84C] me-2">›</span>
            {lines[currentLine].slice(0, currentChar)}
            <span className="inline-block w-[2px] h-3.5 bg-[#C9A84C] ms-0.5 animate-pulse align-middle" />
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── Feature Card: Cursor Protocol Scheduler ─── */
function CursorScheduler() {
  const { t } = useTranslation("landing");
  const [activeDay, setActiveDay] = useState(1);
  const days = [t("scheduler.mon"), t("scheduler.tue"), t("scheduler.wed"), t("scheduler.thu"), t("scheduler.fri"), t("scheduler.sat"), t("scheduler.sun")];
  const goals = [50, 0, 75, 25, 100, 0, 50];

  useEffect(() => {
    const interval = setInterval(() => setActiveDay((d) => (d + 1) % 7), 1200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#0D0D12] border border-[#2A2A35]/40 rounded-[1.5rem] p-5 h-64">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-4 h-4 text-[#C9A84C]" />
        <span className="text-xs font-mono text-[#FAF8F5]/40">{t("scheduler.weeklyPlan")}</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5 h-40">
        {days.map((day, i) => (
          <div key={day} className="flex flex-col items-center justify-end gap-1">
            <div
              className="w-full rounded-lg transition-all duration-500 ease-out"
              style={{
                height: `${Math.max(goals[i], 8)}%`,
                backgroundColor: i === activeDay ? "#C9A84C" : "#2A2A35",
                opacity: i === activeDay ? 1 : 0.5,
                transform: i === activeDay ? "scaleY(1.05)" : "scaleY(1)",
              }}
            />
            <span className={`text-[10px] font-mono transition-colors ${i === activeDay ? "text-[#C9A84C]" : "text-[#FAF8F5]/30"}`}>
              {day}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Features Section ─── */
function FeaturesSection() {
  const { t } = useTranslation("landing");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".feature-card",
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.15, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", toggleActions: "play none none none" },
        }
      );
    }, sectionRef);
    const timer = setTimeout(() => {
      sectionRef.current?.querySelectorAll(".feature-card").forEach((el) => {
        (el as HTMLElement).style.opacity = "1";
        (el as HTMLElement).style.transform = "translateY(0)";
      });
    }, 2000);
    return () => { ctx.revert(); clearTimeout(timer); };
  }, []);

  return (
    <section ref={sectionRef} id="features" className="py-32 px-6 relative bg-[#0D0D12]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <p className="text-xs font-mono text-[#C9A84C]/70 tracking-[0.2em] uppercase mb-4">{t("features.eyebrow")}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#FAF8F5] tracking-tight">
            {t("features.titleLine1")}<br />
            <span className="font-['Playfair_Display'] italic text-[#C9A84C]">{t("features.titleHighlight")}</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Card 1 - Diagnostic Shuffler */}
          <div className="feature-card group">
            <div className="bg-[#1a1a24]/50 border border-[#2A2A35]/40 rounded-[2rem] p-6 hover:border-[#C9A84C]/20 transition-all duration-300 hover:-translate-y-1">
              <DiagnosticShuffler />
              <h3 className="text-xl font-semibold text-[#FAF8F5] mt-6 mb-2">{t("features.card1Title")}</h3>
              <p className="text-sm text-[#FAF8F5]/70">{t("features.card1Desc")}</p>
            </div>
          </div>

          {/* Card 2 - Telemetry Typewriter */}
          <div className="feature-card group">
            <div className="bg-[#1a1a24]/50 border border-[#2A2A35]/40 rounded-[2rem] p-6 hover:border-[#C9A84C]/20 transition-all duration-300 hover:-translate-y-1">
              <TelemetryTypewriter />
              <h3 className="text-xl font-semibold text-[#FAF8F5] mt-6 mb-2">{t("features.card2Title")}</h3>
              <p className="text-sm text-[#FAF8F5]/70">{t("features.card2Desc")}</p>
            </div>
          </div>

          {/* Card 3 - Cursor Scheduler */}
          <div className="feature-card group">
            <div className="bg-[#1a1a24]/50 border border-[#2A2A35]/40 rounded-[2rem] p-6 hover:border-[#C9A84C]/20 transition-all duration-300 hover:-translate-y-1">
              <CursorScheduler />
              <h3 className="text-xl font-semibold text-[#FAF8F5] mt-6 mb-2">{t("features.card3Title")}</h3>
              <p className="text-sm text-[#FAF8F5]/70">{t("features.card3Desc")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Companion Section ─── */
function CompanionSection() {
  const { t } = useTranslation("landing");
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeStage, setActiveStage] = useState(0);
  const stages = [
    { stage: "egg", name: t("companion.stages.egg.name"), desc: t("companion.stages.egg.desc"), days: t("companion.stages.egg.days") },
    { stage: "baby", name: t("companion.stages.baby.name"), desc: t("companion.stages.baby.desc"), days: t("companion.stages.baby.days") },
    { stage: "common", name: t("companion.stages.common.name"), desc: t("companion.stages.common.desc"), days: t("companion.stages.common.days") },
    { stage: "epic", name: t("companion.stages.epic.name"), desc: t("companion.stages.epic.desc"), days: t("companion.stages.epic.days") },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".companion-title",
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", toggleActions: "play none none none" },
        }
      );
      gsap.fromTo(".stage-card",
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.12, duration: 0.6, ease: "power3.out",
          scrollTrigger: { trigger: ".stage-grid", start: "top 85%", toggleActions: "play none none none" },
        }
      );
    }, sectionRef);
    const timer = setTimeout(() => {
      sectionRef.current?.querySelectorAll(".companion-title, .stage-card").forEach((el) => {
        (el as HTMLElement).style.opacity = "1";
        (el as HTMLElement).style.transform = "translateY(0)";
      });
    }, 2000);
    return () => { ctx.revert(); clearTimeout(timer); };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setActiveStage((s) => (s + 1) % stages.length), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={sectionRef} id="companion" className="py-32 px-6 bg-gradient-to-b from-[#0D0D12] to-[#12121a]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-20 companion-title">
          <p className="text-xs font-mono text-[#C9A84C]/70 tracking-[0.2em] uppercase mb-4">{t("companion.eyebrow")}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#FAF8F5] tracking-tight mb-4">
            {t("companion.titleLine1")}<br />
            <span className="font-['Playfair_Display'] italic text-[#C9A84C]">{t("companion.titleHighlight")}</span>
          </h2>
          <p className="text-lg text-[#FAF8F5]/70 max-w-2xl mx-auto">
            {t("companion.desc")}
          </p>
        </div>

        {/* Evolution visualization */}
        <div className="stage-grid relative mb-16">
          {/* Connection line */}
          <div className="hidden md:block absolute top-1/2 start-[10%] end-[10%] h-px bg-gradient-to-r from-transparent via-[#C9A84C]/30 to-transparent -translate-y-1/2" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stages.map((stage, i) => (
              <div
                key={stage.name}
                className={`stage-card relative bg-[#1a1a24]/60 border rounded-[2rem] p-6 text-center transition-all duration-500 cursor-pointer
                  ${activeStage === i ? "border-[#C9A84C]/50 shadow-lg shadow-[#C9A84C]/10 -translate-y-1" : "border-[#2A2A35]/40"}`}
                onClick={() => setActiveStage(i)}
              >
                <div className={`mb-4 flex justify-center transition-transform duration-500 ${activeStage === i ? "scale-110" : ""}`}>
                  <img
                    src={getFennieArtwork(stage.stage, "healthy")}
                    alt={`Fennie ${stage.name}`}
                    className="w-20 h-20 object-contain"
                    draggable={false}
                  />
                </div>
                <p className="font-semibold text-[#FAF8F5] text-sm mb-1">{stage.name}</p>
                <p className="text-xs text-[#FAF8F5]/35">{stage.days}</p>
                <p className="text-xs text-[#C9A84C]/60 mt-2">{stage.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Evolution triggers */}
        <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            { icon: BarChart3, text: t("companion.trigger1") },
            { icon: Target, text: t("companion.trigger2") },
            { icon: Sparkles, text: t("companion.trigger3") },
          ].map((trigger) => (
            <div key={trigger.text} className="flex items-center gap-3 bg-[#1a1a24]/40 border border-[#2A2A35]/30 rounded-[1.5rem] px-5 py-4">
              <trigger.icon className="w-4 h-4 text-[#C9A84C] shrink-0" />
              <span className="text-sm text-[#FAF8F5]/60">{trigger.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Philosophy / Manifesto ─── */
function PhilosophySection() {
  const { t } = useTranslation("landing");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".manifesto-small",
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", toggleActions: "play none none none" },
        }
      );
      gsap.fromTo(".manifesto-large",
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", toggleActions: "play none none none" },
        }
      );
    }, sectionRef);
    const timer = setTimeout(() => {
      sectionRef.current?.querySelectorAll(".manifesto-small, .manifesto-large").forEach((el) => {
        (el as HTMLElement).style.opacity = "1";
        (el as HTMLElement).style.transform = "translateY(0)";
      });
    }, 2000);
    return () => { ctx.revert(); clearTimeout(timer); };
  }, []);

  return (
    <section ref={sectionRef} className="py-40 px-6 bg-[#0D0D12] relative">
      <div className="max-w-4xl mx-auto text-center">
        <p className="manifesto-small text-lg sm:text-xl text-[#FAF8F5]/70 mb-6">
          {t("philosophy.small")}
        </p>
        <h2 className="manifesto-large text-4xl sm:text-5xl md:text-6xl font-bold text-[#FAF8F5] tracking-tight leading-tight">
          {t("philosophy.titlePrefix")}{" "}
          <span className="font-['Playfair_Display'] italic text-[#C9A84C]">
            {t("philosophy.titleHighlight")}
          </span>
        </h2>
      </div>
    </section>
  );
}

/* ─── Protocol / Sticky Cards ─── */
function ProtocolSection() {
  const { t } = useTranslation("landing");
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps = [
    {
      step: "01",
      title: t("protocol.step1.title"),
      description: t("protocol.step1.description"),
      icon: Wallet,
      visual: (
        <div className="bg-[#1a1a24]/60 rounded-2xl border border-[#2A2A35]/40 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#2A2A35]/30 flex items-center justify-between">
            <span className="text-xs font-mono text-[#FAF8F5]/50 tracking-wide">{t("protocol.visual1.recentTransactions")}</span>
            <span className="text-[10px] text-[#C9A84C]/60 font-mono">{t("protocol.visual1.today")}</span>
          </div>
          <div className="divide-y divide-[#2A2A35]/20">
            {[
              { label: t("protocol.visual1.coffee"), amount: "-$4.50", cat: "☕", category: t("protocol.visual1.catDining") },
              { label: t("protocol.visual1.groceries"), amount: "-$67.20", cat: "🛒", category: t("protocol.visual1.catGroceries") },
              { label: t("protocol.visual1.netflix"), amount: "-$15.99", cat: "📺", category: t("protocol.visual1.catSubscriptions") },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{item.cat}</span>
                  <div>
                    <p className="text-sm text-[#FAF8F5]/80">{item.label}</p>
                    <span className="text-[10px] text-[#FAF8F5]/30 font-mono bg-[#2A2A35]/40 px-1.5 py-0.5 rounded">{item.category}</span>
                  </div>
                </div>
                <span className="font-mono text-sm text-[#C9A84C]">{item.amount}</span>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 border-t border-[#2A2A35]/30 flex items-center gap-2">
            <div className="flex-1 bg-[#2A2A35]/30 rounded-lg px-3 py-1.5 text-xs text-[#FAF8F5]/25">{t("protocol.visual1.quickAdd")}</div>
            <div className="w-7 h-7 rounded-lg bg-[#C9A84C]/20 flex items-center justify-center">
              <Mic className="w-3.5 h-3.5 text-[#C9A84C]" />
            </div>
          </div>
        </div>
      ),
    },
    {
      step: "02",
      title: t("protocol.step2.title"),
      description: t("protocol.step2.description"),
      icon: Eye,
      visual: (
        <div className="space-y-4">
          <div className="space-y-3">
            {[
              { label: t("protocol.visual2.catDining"), pct: 85, amount: "$320", color: "#C9A84C" },
              { label: t("protocol.visual2.catTransport"), pct: 45, amount: "$168", color: "#FAF8F5" },
              { label: t("protocol.visual2.catShopping"), pct: 65, amount: "$245", color: "#C9A84C" },
            ].map((bar) => (
              <div key={bar.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#FAF8F5]/50">{bar.label}</span>
                  <span className="font-mono text-[#FAF8F5]/40">{bar.amount}</span>
                </div>
                <div className="h-2 bg-[#2A2A35]/60 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${bar.pct}%`, backgroundColor: bar.color }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-xl px-4 py-3 flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-[#C9A84C] font-semibold">{t("protocol.visual2.aiInsight")}</p>
              <p className="text-[11px] text-[#FAF8F5]/50 mt-0.5 leading-relaxed font-mono">{t("protocol.visual2.aiInsightText")}</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      step: "03",
      title: t("protocol.step3.title"),
      description: t("protocol.step3.description"),
      icon: Target,
      visual: (
        <div className="space-y-4">
          <div className="bg-[#1a1a24]/60 rounded-xl border border-[#2A2A35]/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#FAF8F5]/70">{t("protocol.visual3.emergencyFund")}</span>
              <span className="font-mono text-xs text-[#C9A84C]">31%</span>
            </div>
            <div className="h-2.5 bg-[#2A2A35]/60 rounded-full overflow-hidden mb-2">
              <div className="h-full rounded-full bg-gradient-to-r from-[#C9A84C] to-[#C9A84C]/60" style={{ width: "31%" }} />
            </div>
            <div className="flex justify-between">
              <span className="font-mono text-xs text-[#FAF8F5]/30">$312</span>
              <span className="font-mono text-xs text-[#FAF8F5]/30">$1,000</span>
            </div>
          </div>
          <div className="bg-[#1a1a24]/60 rounded-xl border border-[#2A2A35]/40 p-4">
            <p className="text-[10px] text-[#FAF8F5]/30 font-mono tracking-wider uppercase mb-3">{t("protocol.visual3.fennieEvolution")}</p>
            <div className="flex items-center justify-between">
              {[
                { stage: "egg", label: t("protocol.visual3.egg"), active: true },
                { stage: "baby", label: t("protocol.visual3.youngFox"), active: true },
                { stage: "common", label: t("protocol.visual3.explorer"), active: false },
                { stage: "epic", label: t("protocol.visual3.guardian"), active: false },
              ].map((s, i) => (
                <div key={s.label} className="flex flex-col items-center gap-1.5 relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${s.active ? 'bg-[#C9A84C]/20 border border-[#C9A84C]/40' : 'bg-[#2A2A35]/30 border border-[#2A2A35]/30 opacity-40'}`}>
                    <img
                      src={getFennieArtwork(s.stage, "healthy")}
                      alt={`Fennie ${s.label}`}
                      className="w-8 h-8 object-contain"
                      draggable={false}
                    />
                  </div>
                  <span className={`text-[9px] font-mono ${s.active ? 'text-[#C9A84C]/70' : 'text-[#FAF8F5]/20'}`}>{s.label}</span>
                  {i < 3 && (
                    <div className={`absolute top-5 start-[calc(100%)] w-4 h-px ${i < 1 ? 'bg-[#C9A84C]/40' : 'bg-[#2A2A35]/30'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll(".protocol-card");
    if (!cards?.length) return;
    // Ensure cards are visible immediately as fallback
    const ctx = gsap.context(() => {
      gsap.fromTo(".protocol-card",
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.15,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );
    }, sectionRef);
    // Safety fallback: ensure visible after timeout
    const timer = setTimeout(() => {
      cards.forEach((card) => {
        (card as HTMLElement).style.opacity = "1";
        (card as HTMLElement).style.transform = "translateY(0)";
      });
    }, 2000);
    return () => { ctx.revert(); clearTimeout(timer); };
  }, []);

  return (
    <section ref={sectionRef} className="py-32 px-6 bg-gradient-to-b from-[#12121a] to-[#0D0D12]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-20">
          <p className="text-xs font-mono text-[#C9A84C]/70 tracking-[0.2em] uppercase mb-4">{t("protocol.eyebrow")}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#FAF8F5] tracking-tight">
            {t("protocol.titleLine1")}{" "}
            <span className="font-['Playfair_Display'] italic text-[#C9A84C]">{t("protocol.titleHighlight")}</span>
          </h2>
          <p className="mt-4 text-[#FAF8F5]/70 max-w-xl mx-auto leading-relaxed">
            {t("protocol.desc")}
          </p>
        </div>

        <div className="space-y-8">
          {steps.map((step) => (
            <div
              key={step.step}
              className="protocol-card bg-[#1a1a24]/50 border border-[#2A2A35]/40 rounded-[2.5rem] p-8 md:p-12
                hover:border-[#C9A84C]/20 transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono text-xs text-[#C9A84C]/50">{step.step}</span>
                    <step.icon className="w-5 h-5 text-[#C9A84C]" />
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-bold text-[#FAF8F5] mb-4">{step.title}</h3>
                  <p className="text-[#FAF8F5]/70 leading-relaxed">{step.description}</p>
                </div>
                <div className="flex-1 w-full max-w-sm">{step.visual}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing Section ─── */
function PricingSection({ onCTA }: { onCTA: () => void }) {
  const { t } = useTranslation("landing");
  const sectionRef = useRef<HTMLDivElement>(null);
  const [yearly, setYearly] = useState(false);

  const tiers = [
    {
      name: t("pricingSection.essential.name"),
      price: t("pricingSection.essential.price"),
      fullPrice: "",
      period: "",
      description: t("pricingSection.essential.description"),
      features: t("pricingSection.essential.features", { returnObjects: true }) as string[],
      cta: t("pricingSection.essential.cta"),
      highlighted: false,
      launchBadge: false,
    },
    (() => {
      const opt = getPlanOption('premium', yearly ? 'yearly' : 'monthly');
      return {
        name: opt.name,
        price: opt.headlinePrice,
        fullPrice: "",
        period: opt.period,
        annualNote: yearly ? opt.subtext : null,
        description: t("pricingSection.individual.description"),
        features: t("pricingSection.individual.features", { returnObjects: true }) as string[],
        cta: t("pricingSection.individual.cta"),
        highlighted: true,
        launchBadge: true,
      };
    })(),
    (() => {
      const opt = getPlanOption('family', yearly ? 'yearly' : 'monthly');
      return {
        name: opt.name,
        price: opt.headlinePrice,
        fullPrice: "",
        period: opt.period,
        annualNote: yearly ? opt.subtext : null,
        description: t("pricingSection.family.description"),
        features: (t("pricingSection.family.features", { returnObjects: true }) as string[]).map((f) =>
          f.includes("{count}") ? f.replace("{count}", String(opt.includedUsers)) : f
        ),
        cta: t("pricingSection.family.cta"),
        highlighted: false,
        launchBadge: true,
      };
    })(),
  ];

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll(".pricing-card");
    if (!cards?.length) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".pricing-card",
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.15,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", toggleActions: "play none none none" },
        }
      );
    }, sectionRef);
    const timer = setTimeout(() => {
      cards.forEach((card) => {
        (card as HTMLElement).style.opacity = "1";
        (card as HTMLElement).style.transform = "translateY(0)";
      });
    }, 2000);
    return () => { ctx.revert(); clearTimeout(timer); };
  }, []);

  return (
    <section ref={sectionRef} id="pricing" className="py-32 px-6 bg-[#0D0D12]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-20">
          <p className="text-xs font-mono text-[#C9A84C]/70 tracking-[0.2em] uppercase mb-4">{t("pricingSection.eyebrow")}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#FAF8F5] tracking-tight">
            {t("pricingSection.titlePrefix")}{" "}
            <span className="font-['Playfair_Display'] italic text-[#C9A84C]">{t("pricingSection.titleHighlight")}</span>{" "}
            {t("pricingSection.titleSuffix")}
          </h2>
          <p className="mt-4 text-[#FAF8F5]/70 max-w-xl mx-auto leading-relaxed">
            {t("pricingSection.desc")}
          </p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm ${!yearly ? 'text-[#FAF8F5]' : 'text-[#FAF8F5]/40'}`}>{t("pricingSection.monthly")}</span>
            <button
              onClick={() => setYearly(!yearly)}
              className={`relative w-12 h-6 rounded-full transition-colors ${yearly ? 'bg-[#C9A84C]' : 'bg-[#2A2A35]'}`}
            >
              <span className={`absolute top-0.5 start-0.5 w-5 h-5 rounded-full bg-[#FAF8F5] shadow transition-transform ${yearly ? 'rtl:-translate-x-6 translate-x-6' : ''}`} />
            </button>
            <span className={`text-sm ${yearly ? 'text-[#FAF8F5]' : 'text-[#FAF8F5]/40'}`}>
              {t("pricingSection.yearly")} <span className="text-[#C9A84C] text-xs font-semibold">{t("pricingSection.save35")}</span>
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`pricing-card rounded-[2rem] p-8 transition-all duration-300 hover:-translate-y-1 ${
                tier.highlighted
                  ? "bg-gradient-to-b from-[#C9A84C]/10 to-[#1a1a24]/60 border-2 border-[#C9A84C]/30 shadow-xl shadow-[#C9A84C]/10 relative"
                  : "bg-[#1a1a24]/50 border border-[#2A2A35]/40"
              }`}
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-[#0D0D12] text-xs font-semibold px-4 py-1 rounded-full">
                  {t("pricingSection.mostPopular")}
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-[#FAF8F5]">{tier.name}</h3>
                {tier.launchBadge && (
                  <span className="text-[10px] font-semibold bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]/30 px-2 py-0.5 rounded-full">
                    {t("pricingSection.launchOffer")}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-bold text-[#FAF8F5]">{tier.price}</span>
                {tier.period && <span className="text-[#FAF8F5]/40 text-sm">{tier.period}</span>}
              </div>
              {'annualNote' in tier && tier.annualNote && (
                <p className="text-xs text-[#FAF8F5]/30 mb-1">{tier.annualNote}</p>
              )}
              <p className="text-sm text-[#FAF8F5]/70 mb-6">{tier.description}</p>
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-[#FAF8F5]/60">
                    <Check className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={onCTA}
                className={`w-full py-3 rounded-[1.5rem] font-semibold text-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] ${
                  tier.highlighted
                    ? "bg-[#C9A84C] text-[#0D0D12]"
                    : "bg-[#2A2A35] text-[#FAF8F5] hover:bg-[#2A2A35]/80"
                }`}
              >
                {tier.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ Section ─── */
function FAQSection() {
  const { t } = useTranslation("landing");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4", { count: PLANS.family.includedUsers }) },
    { q: t("faq.q5"), a: t("faq.a5") },
    { q: t("faq.q6"), a: t("faq.a6", { name: PLANS.premium.displayName, price: formatPlanPriceShort('premium', 'yearly'), familyPrice: formatPlanPriceShort('family', 'yearly'), count: PLANS.family.includedUsers }) },
  ];

  return (
    <section id="faq" className="py-32 px-6 bg-gradient-to-b from-[#0D0D12] to-[#12121a]">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs font-mono text-[#C9A84C]/70 tracking-[0.2em] uppercase mb-4">{t("faq.eyebrow")}</p>
          <h2 className="text-4xl font-bold text-[#FAF8F5] tracking-tight">{t("faq.title")}</h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="border border-[#2A2A35]/40 rounded-[1.5rem] overflow-hidden transition-colors hover:border-[#2A2A35]/60"
            >
              <button
                className="w-full flex items-center justify-between p-5 text-start"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="text-sm font-medium text-[#FAF8F5]/80">{faq.q}</span>
                <ChevronDown
                  className={`w-4 h-4 text-[#C9A84C] shrink-0 ms-4 transition-transform duration-300 rtl:rotate-180 ${openIndex === i ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-out"
                style={{ maxHeight: openIndex === i ? "200px" : "0" }}
              >
                <p className="px-5 pb-5 text-sm text-[#FAF8F5]/75 leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function LandingFooter() {
  const { t } = useTranslation("landing");
  return (
    <footer className="py-16 px-6 bg-[#0D0D12]">
      <div className="max-w-5xl mx-auto">
        <div className="bg-[#1a1a24]/40 border border-[#2A2A35]/30 rounded-[2.5rem] p-10 md:p-14">
          <div className="flex flex-col md:flex-row justify-between items-start gap-10">
            {/* Brand */}
            <div>
              <span className="text-xl font-semibold text-[#FAF8F5] tracking-tight">{t("nav.brand")}</span>
              <p className="text-sm text-[#FAF8F5]/70 mt-2 max-w-xs">
                {t("footer.tagline")}
              </p>
            </div>

            {/* Navigation */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-16 gap-y-4 text-sm">
              <div className="space-y-3">
                <p className="text-[#FAF8F5]/20 font-mono text-xs uppercase tracking-wider">{t("footer.productHeading")}</p>
                <a href="#features" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.features")}</a>
                <a href="#companion" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.companion")}</a>
                <a href="#pricing" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.pricing")}</a>
                <Link to="/creators" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.creatorProgram")}</Link>
              </div>
              <div className="space-y-3">
                <p className="text-[#FAF8F5]/20 font-mono text-xs uppercase tracking-wider">{t("footer.legalHeading")}</p>
                <Link to="/privacy" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.privacy")}</Link>
                <Link to="/terms" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.terms")}</Link>
                <Link to="/refund" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.refund")}</Link>
              </div>
              <div className="space-y-3">
                <p className="text-[#FAF8F5]/20 font-mono text-xs uppercase tracking-wider">{t("footer.accountHeading")}</p>
                <Link to="/auth" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.signIn")}</Link>
                <Link to="/auth?tab=signup" className="block text-[#FAF8F5]/50 hover:text-[#C9A84C] transition-colors">{t("footer.signUp")}</Link>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 pt-8 border-t border-[#2A2A35]/30 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#FAF8F5]/20">{t("footer.copyright")}</p>
            <div className="flex items-center gap-2 text-xs text-[#FAF8F5]/20">
              <CircleDot className="w-3 h-3 text-emerald-500" />
              {t("footer.systemOperational")}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Main Landing Page ─── */
export default function Landing() {
  const { t } = useTranslation("landing");
  const { user, loading } = useAuth();
  const authRef = useRef<HTMLDivElement>(null);

  const [authTab, setAuthTab] = useState<"signin" | "signup">("signin");
  const scrollToAuth = useCallback(() => {
    setAuthTab("signup");
    authRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D12]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { q: "Do I need to connect my bank account?", a: "No. Fennest is privacy-first. You manually log expenses or scan receipts. No bank connection is ever required." },
      { q: "What is the financial companion system?", a: "Fennie the Fennec Fox is a behavioral companion that evolves as your financial habits improve. It reflects your consistency, not a game score. The full evolution system is free for all users." },
      { q: "Is there a free plan?", a: "Yes. The Essential plan is free forever and includes expense tracking, 1 budget, 1 savings goal, bill alerts, full Fennie evolution, and basic insights." },
      { q: "Can I use Fennest with my partner or family?", a: "Yes. The Family plan supports multiple members with shared accountability and all premium features." },
      { q: "How does the AI work?", a: "Fennest uses AI to analyze your spending patterns, categorize expenses, scan receipts, and provide personalized insights. All processing is secure and private." },
      { q: "What does premium include?", a: "The Individual plan includes AI spending analysis, voice assistant, unlimited receipt scans, and full journey history. The Family plan adds shared accountability for multiple people." },
    ].map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-[#0D0D12] text-[#FAF8F5] overflow-x-hidden">
      <Seo
        title="Fennest - Money clarity, with intelligence"
        description="Privacy-first personal finance for individuals, couples, and families. Track spending, set goals, and build healthier money habits with Fennie."
        path="/"
        jsonLd={faqJsonLd}
      />
      <NoiseOverlay />
      <LandingNav onCTA={scrollToAuth} />
      <HeroSection onCTA={scrollToAuth} />
      <FeaturesSection />
      <CompanionSection />
      <PhilosophySection />
      <ProtocolSection />
      <PricingSection onCTA={scrollToAuth} />
      <FAQSection />
      <RoadmapPreview />

      {/* Auth / CTA Section */}
      <section ref={authRef} className="py-32 px-6 bg-[#0D0D12] relative">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#FAF8F5] mb-4 tracking-tight">
            {t("cta.title")}
          </h2>
          <p className="text-[#FAF8F5]/40 mb-8">{t("cta.subtitle")}</p>

          <div className="bg-[#1a1a24]/60 border border-[#2A2A35]/40 rounded-[2rem] p-8">
            <Suspense fallback={<div className="h-64 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" /></div>}>
              <AuthForm redirectTo="/dashboard" tab={authTab} onTabChange={setAuthTab} />
            </Suspense>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs text-[#FAF8F5]/25">
            <span className="flex items-center gap-1.5"><Shield className="w-3 h-3" /> {t("cta.encryption")}</span>
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> {t("cta.gdpr")}</span>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
